import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState({
    accessToken: null,
    idToken: null,
    refreshToken: null
  });

  // Memoized functions with stable dependencies
  const logout = useCallback(() => {
    console.log('Logging out user');
    authService.clearStoredTokens();
    setIsAuthenticated(false);
    setUser(null);
    setTokens({ accessToken: null, idToken: null, refreshToken: null });
    
    // Redirect to Cognito logout which will clear Cognito session
    authService.redirectToLogout();
  }, []);

  const login = useCallback(() => {
    console.log('Redirecting to Cognito login');
    authService.redirectToLogin(true); // Always force login
  }, []);

  const refreshTokens = useCallback(async () => {
    try {
      console.log('Attempting to refresh tokens');
      const storedTokens = authService.getStoredTokens();
      if (storedTokens.refreshToken) {
        const newTokens = await authService.refreshTokens(storedTokens.refreshToken);
        setTokens(newTokens);
        authService.storeTokens(newTokens);
        
        const userInfo = await authService.getUserInfo(newTokens.accessToken);
        setUser(userInfo);
        setIsAuthenticated(true);
        console.log('Tokens refreshed successfully');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  }, [logout]);

  const checkAuthState = useCallback(async () => {
    try {
      console.log('Checking auth state');
      const storedTokens = authService.getStoredTokens();
      
      if (storedTokens.accessToken && authService.isTokenValid(storedTokens.accessToken)) {
        console.log('Valid access token found');
        setTokens(storedTokens);
        const userInfo = await authService.getUserInfo(storedTokens.accessToken);
        setUser(userInfo);
        setIsAuthenticated(true);
      } else if (storedTokens.refreshToken) {
        console.log('Access token expired, trying refresh token');
        await refreshTokens();
      } else {
        console.log('No valid tokens found');
        // Don't call logout here to avoid redirect loop
        setIsAuthenticated(false);
        setUser(null);
        setTokens({ accessToken: null, idToken: null, refreshToken: null });
      }
    } catch (error) {
      console.error('Auth state check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      setTokens({ accessToken: null, idToken: null, refreshToken: null });
    } finally {
      setLoading(false);
    }
  }, [refreshTokens]);

  const handleCallback = useCallback(async (code) => {
    try {
      console.log('Handling OAuth callback with code:', code);
      setLoading(true);
      const tokenResponse = await authService.exchangeCodeForTokens(code);
      setTokens(tokenResponse);
      authService.storeTokens(tokenResponse);
      
      const userInfo = await authService.getUserInfo(tokenResponse.accessToken);
      setUser(userInfo);
      setIsAuthenticated(true);
      
      console.log('Callback handled successfully');
      return true;
    } catch (error) {
      console.error('Callback handling failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Only run once on mount - empty dependency array
  useEffect(() => {
    checkAuthState();
  }, []);

  const value = {
    isAuthenticated,
    user,
    tokens,
    loading,
    login,
    logout,
    handleCallback,
    refreshTokens
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};